import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './Auth.css';

function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        label: '',
        color: '',
        checks: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false
        }
    });

    const [verifying, setVerifying] = useState(true);

    useEffect(() => {
        const verifyToken = async () => {
            const tokenFromUrl = searchParams.get('token');
            if (!tokenFromUrl) {
                setError('Ugyldig lenke. Vennligst be om tilbakestilling av passord på nytt.');
                setVerifying(false);
                return;
            }
            
            setToken(tokenFromUrl);
            
            try {
                const response = await fetch(`${API_ENDPOINTS.VERIFY_TOKEN}?token=${tokenFromUrl}`);
                const data = await response.json();
                
                if (!data.valid) {
                    setError(data.error || 'Denne lenken er ugyldig eller har utløpt.');
                }
            } catch (err) {
                setError('Tilkoblingsfeil. Kunne ikke verifisere lenken.');
            } finally {
                setVerifying(false);
            }
        };
        
        verifyToken();
    }, [searchParams]);

    // Calculate password strength
    useEffect(() => {
        if (!password) {
            setPasswordStrength({
                score: 0,
                label: '',
                color: '',
                checks: {
                    length: false,
                    uppercase: false,
                    lowercase: false,
                    number: false,
                    special: false
                }
            });
            return;
        }

        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        const score = Object.values(checks).filter(Boolean).length;

        let label = '';
        let color = '';

        if (score === 0) {
            label = '';
            color = '';
        } else if (score <= 2) {
            label = 'Svak';
            color = '#ef4444';
        } else if (score === 3) {
            label = 'Middels';
            color = '#f59e0b';
        } else if (score === 4) {
            label = 'God';
            color = '#3b82f6';
        } else {
            label = 'Sterk';
            color = '#10b981';
        }

        setPasswordStrength({ score, label, color, checks });
    }, [password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('Ugyldig tilbakestillingslenke');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passordene samsvarer ikke');
            return;
        }

        if (passwordStrength.score < 5) {
            setError('Passordet må oppfylle alle krav');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password }),
            });

            const data = await response.json();

            if (response.ok) {
                navigate('/login', { state: { message: 'Passordet ble tilbakestilt. Logg inn med ditt nye passord.' } });
            } else {
                setError(data.error || 'Kunne ikke tilbakestille passordet');
            }
        } catch (err) {
            setError('Tilkoblingsfeil. Vennligst sjekk internettforbindelsen din.');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (verifying) {
            return (
                <div className="verifying-container">
                    <span className="spinner"></span>
                    <p>Verifiserer lenke...</p>
                </div>
            );
        }

        if (error && !token) {
            return (
                <div className="error-container">
                    <div className="error-message">{error}</div>
                    <div className="auth-footer" style={{ marginTop: '20px' }}>
                        <p><Link to="/forgot-password">Be om tilbakestilling på nytt</Link></p>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="auth-header">
                    <h2>Tilbakestill passord</h2>
                    <p>Skriv inn et nytt passord</p>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>Nytt passord</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={loading || !token}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex="-1"
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>

                        {/* Password Strength Meter */}
                        {password && (
                            <div className="password-strength">
                                <div className="strength-bars">
                                    {[1, 2, 3, 4, 5].map((bar) => (
                                        <div
                                            key={bar}
                                            className={`strength-bar ${bar <= passwordStrength.score ? 'active' : ''}`}
                                            style={{
                                                backgroundColor: bar <= passwordStrength.score ? passwordStrength.color : '#e5e7eb'
                                            }}
                                        ></div>
                                    ))}
                                </div>
                                {passwordStrength.label && (
                                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                                        {passwordStrength.label}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Password Requirements */}
                        {password && (
                            <div className="password-requirements">
                                <div className={`requirement ${passwordStrength.checks.length ? 'met' : ''}`}>
                                    {passwordStrength.checks.length ? '✓' : '○'} Minst 8 tegn
                                </div>
                                <div className={`requirement ${passwordStrength.checks.uppercase ? 'met' : ''}`}>
                                    {passwordStrength.checks.uppercase ? '✓' : '○'} Minst én stor bokstav
                                </div>
                                <div className={`requirement ${passwordStrength.checks.lowercase ? 'met' : ''}`}>
                                    {passwordStrength.checks.lowercase ? '✓' : '○'} Minst én liten bokstav
                                </div>
                                <div className={`requirement ${passwordStrength.checks.number ? 'met' : ''}`}>
                                    {passwordStrength.checks.number ? '✓' : '○'} Minst ett tall
                                </div>
                                <div className={`requirement ${passwordStrength.checks.special ? 'met' : ''}`}>
                                    {passwordStrength.checks.special ? '✓' : '○'} Minst ett spesialtegn
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Bekreft nytt passord</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={loading || !token}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex="-1"
                            >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {confirmPassword && password !== confirmPassword && (
                            <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '6px' }}>
                                Passordene samsvarer ikke
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="btn-primary btn-block"
                        disabled={loading || !token || (password && passwordStrength.score < 5) || password !== confirmPassword}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Tilbakestiller...
                            </>
                        ) : 'Tilbakestill passord'}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>Husker du passordet ditt? <Link to="/login">Logg inn</Link></p>
                </div>
            </>
        );
    };

    return (
        <div className="page-container auth-page">
            <Navbar />
            <div className="auth-card glass">
                {renderContent()}
            </div>
        </div>
    );
}

export default ResetPassword;
